'use client';

import { useEffect, useRef } from 'react';
import { Mic, MicOff, PhoneOff, PhoneForwarded, User } from 'lucide-react';
import { Drawer } from '@/shared/ui/Drawer';
import { useStaffCallStore } from '../hooks/useStaffCallClient';

interface ActiveCallPanelProps {
  onHangup: () => void;
  onTransfer: () => void;
}

function formatDuration(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function ActiveCallPanel({ onHangup, onTransfer }: ActiveCallPanelProps) {
  const store = useStaffCallStore();
  const durationRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof requestAnimationFrame> | null>(null);
  const displayRef = useRef<HTMLSpanElement>(null);

  const isActive =
    store.state === 'connected' ||
    store.state === 'connected-muted' ||
    store.state === 'connecting';

  useEffect(() => {
    if (!isActive || !store.activeCall?.connectedAt) return;

    const tick = () => {
      const now = Date.now();
      durationRef.current = Math.round(
        (now - (store.activeCall?.connectedAt ?? now)) / 1000
      );
      if (displayRef.current) {
        displayRef.current.textContent = formatDuration(durationRef.current);
      }
      timerRef.current = requestAnimationFrame(tick);
    };
    timerRef.current = requestAnimationFrame(tick);

    return () => {
      if (timerRef.current) cancelAnimationFrame(timerRef.current);
    };
  }, [isActive, store.activeCall?.connectedAt]);

  return (
    <Drawer
      open={isActive}
      onClose={onHangup}
      side="right"
      width="380px"
      title="Active Call"
      id="calls_active_panel"
      footer={
        <div className="flex gap-3 w-full">
          <button
            id="calls_active_transfer_btn"
            type="button"
            onClick={onTransfer}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-hms-surface text-hms-textSub hover:bg-hms-card transition-colors text-sm font-medium border border-hms-border"
          >
            <PhoneForwarded className="w-4 h-4" />
            Transfer
          </button>
          <button
            id="calls_active_hangup_btn"
            type="button"
            onClick={onHangup}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-hms-red text-white hover:bg-hms-red/90 transition-colors text-sm font-medium"
          >
            <PhoneOff className="w-4 h-4" />
            End Call
          </button>
        </div>
      }
    >
      <div className="flex flex-col items-center gap-5 py-6">
        {/*
          Why a failed transfer went unreported.

          The gateway replies `call:transfer-failed` when the target is
          offline, busy, or at another property, and `setTransferFailed` writes
          the reason into the store. Nothing rendered it: the dialog closed and
          the call simply stayed put, so the staff member believed the transfer
          had gone through and walked away from a guest still on the line.

          Deliberately non-blocking — the call is still theirs and still
          working, so this reports rather than interrupts.
        */}
        {store.transferError && (
          <div
            id="calls_transfer_error"
            role="alert"
            className="w-full px-3 py-2 rounded-lg bg-hms-red/10 border border-hms-red/30 text-center"
          >
            <p className="text-sm text-hms-red">{store.transferError}</p>
            <p className="text-xs text-hms-textDim mt-0.5">
              The call is still with you.
            </p>
          </div>
        )}

        {/* Avatar */}
        <div className="flex items-center justify-center w-16 h-16 rounded-full bg-hms-card border-2 border-hms-green">
          <User className="w-7 h-7 text-hms-green" />
        </div>

        {/* Caller info */}
        <div className="text-center">
          <p className="text-lg font-semibold text-hms-text">
            {store.activeCall?.guestName ?? 'Guest'}
          </p>
          <p className="text-sm text-hms-textSub mt-0.5">
            {store.activeCall?.hotelName}
          </p>
          <p className="text-xs text-hms-textDim mt-1">
            Booking {store.activeCall?.bookingId}
          </p>
        </div>

        {/* Status */}
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-hms-green/10 text-hms-green text-xs font-medium">
          <span className="w-2 h-2 rounded-full bg-hms-green" />
          {store.state === 'connecting' ? 'Connecting...' : 'Connected'}
        </div>

        {/* Recording notice.
            Shown only once the gateway CONFIRMS a recorder is running, not
            because policy permits it — a badge for a recording that failed to
            start is its own dishonesty. Consent stamped in `consent_guest_at`
            is not notice to the people on the call; this is. */}
        {store.isRecording && (
          <div
            id="calls_active_recording"
            className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-hms-red/10 text-hms-red text-xs font-medium"
            role="status"
          >
            <span className="w-2 h-2 rounded-full bg-hms-red animate-pulse" />
            Recording
          </div>
        )}

        {/* Timer */}
        <span
          ref={displayRef}
          id="calls_active_timer"
          className="text-2xl font-mono text-hms-text tabular-nums"
        >
          0:00
        </span>

        {/* Mute toggle */}
        <button
          id="calls_active_mute_btn"
          type="button"
          onClick={store.toggleMute}
          className={`flex items-center justify-center w-12 h-12 rounded-full transition-colors ${
            store.isMuted
              ? 'bg-hms-red/20 text-hms-red'
              : 'bg-hms-surface text-hms-textSub hover:bg-hms-card'
          }`}
          aria-label={store.isMuted ? 'Unmute' : 'Mute'}
        >
          {store.isMuted ? (
            <MicOff className="w-5 h-5" />
          ) : (
            <Mic className="w-5 h-5" />
          )}
        </button>
      </div>
    </Drawer>
  );
}
